import { matchPath } from 'react-router-dom';

import { CAR_RENTAL_PATH_NAME } from '@constants/general';

import {
  flightSearchResultsPaths,
  flightsPaths,
  hotelDetailsPaths,
  hotelSearchResultsPaths,
  hotelsPaths,
} from '@helpers/path';

/**
 * @description Map browser location to a small, stable set of Datadog view names.
 * The goal is to make dashboards and filters consistent across locales and URL variants.
 */
export function getDatadogViewName(locale: string, pathname: string, search: string = ''): string {
  const isFlightResults = flightsPaths.some((path) =>
    flightSearchResultsPaths.some((pattern) =>
      [
        `/${locale}/${path}/${pattern}`,
        `/${path}/${pattern}`,
        `/${locale}/${path}/${pattern}/*`,
        `/${path}/${pattern}/*`,
      ].some((p) => matchPath({ path: p }, pathname)),
    ),
  );

  // Flight booking
  const isFlightPaymentVerification = flightsPaths.some((path) =>
    [`/${locale}/${path}/booking/verify`, `/${path}/booking/verify`].some((pattern) =>
      matchPath({ path: pattern, end: true }, pathname),
    ),
  );

  const isFlightBookingHistoryDetails = flightsPaths.some((path) =>
    [`/${locale}/${path}/booking/history/:bookingRef`, `/${path}/booking/history/:bookingRef`].some(
      (pattern) => matchPath({ path: pattern }, pathname),
    ),
  );

  const isFlightTripDetail = flightsPaths.some((path) =>
    [`/${locale}/${path}/searches/trips/:tripId`, `/${path}/searches/trips/:tripId`].some(
      (pattern) => matchPath({ path: pattern }, pathname),
    ),
  );

  const isFlightTripDetailUser = flightsPaths.some((path) =>
    [`/${locale}/${path}/searches/`, `/${path}/searches/`].some(
      (pattern) => pathname.startsWith(pattern) && search.includes('selected_trip_id='),
    ),
  );

  const isFlightFareComparison = flightsPaths.some((path) =>
    [
      `/${locale}/${path}/searches/:leg/:cabinClass/:passengerCounts/:searchAndTrip/:fareCode`,
      `/${path}/searches/:leg/:cabinClass/:passengerCounts/:searchAndTrip/:fareCode`,
    ].some((pattern) => matchPath({ path: pattern, end: true }, pathname)),
  );

  const isFlightBooking =
    flightsPaths.some((path) =>
      [
        `/${locale}/${path}/searches/*/booking`,
        `/${path}/searches/*/booking`,
        `/${locale}/${path}/booking/*`,
        `/${path}/booking/*`,
      ].some((pattern) => matchPath({ path: pattern }, pathname)),
    ) ||
    (flightsPaths.some((path) => pathname.includes(`/${path}/`)) &&
      pathname.includes('/booking') &&
      !pathname.includes('/booking/confirmation') &&
      !pathname.includes('/booking/verify') &&
      !pathname.includes('/booking/history'));

  const isFlightConfirmation = flightsPaths.some((path) =>
    [`/${locale}/${path}/booking/confirmation`, `/${path}/booking/confirmation`].some((pattern) =>
      pathname.startsWith(pattern),
    ),
  );

  const isHotelResults = hotelsPaths.some((path) =>
    hotelSearchResultsPaths.some(
      (pattern) =>
        matchPath({ path: `/${locale}/${path}/${pattern}` }, pathname) ||
        matchPath({ path: `/${path}/${pattern}` }, pathname),
    ),
  );

  const isHotelDetail = hotelsPaths.some((path) =>
    hotelDetailsPaths.some(
      (pattern) =>
        matchPath({ path: `/${locale}/${path}/${pattern}` }, pathname) ||
        matchPath({ path: `/${path}/${pattern}` }, pathname),
    ),
  );

  const isHotelCheckout = hotelsPaths.some((path) =>
    [`/${locale}/${path}/booking/checkout`, `/${path}/booking/checkout`].some((pattern) =>
      matchPath({ path: pattern, end: true }, pathname),
    ),
  );

  const isHotelConfirm = hotelsPaths.some((path) =>
    [`/${locale}/${path}/booking/confirm`, `/${path}/booking/confirm`].some((pattern) =>
      matchPath({ path: pattern, end: true }, pathname),
    ),
  );

  const isHotelReview = hotelsPaths.some((path) =>
    [`/${locale}/${path}/booking/review`, `/${path}/booking/review`].some((pattern) =>
      matchPath({ path: pattern, end: true }, pathname),
    ),
  );

  const isFlightsHome = flightsPaths.some(
    (path) =>
      matchPath({ path: `/${locale}/${path}`, end: true }, pathname) ||
      matchPath({ path: `/${path}`, end: true }, pathname),
  );

  const isHotelsHome = hotelsPaths.some(
    (path) =>
      matchPath({ path: `/${locale}/${path}`, end: true }, pathname) ||
      matchPath({ path: `/${path}`, end: true }, pathname),
  );

  const isProfilePaymentMethods =
    matchPath({ path: `/preferences/profile/payment-methods` }, pathname) ||
    matchPath({ path: `/${locale}/preferences/profile/payment-methods` }, pathname);

  const isProfilePersonalInfo =
    matchPath({ path: `/preferences/profile/personal-info` }, pathname) ||
    matchPath({ path: `/${locale}/preferences/profile/personal-info` }, pathname);

  const isProfileTravellers =
    matchPath({ path: `/preferences/profile/travellers` }, pathname) ||
    matchPath({ path: `/${locale}/preferences/profile/travellers` }, pathname);

  const isHotelsBookingHistory =
    matchPath({ path: `/preferences/trips/hotels` }, pathname) ||
    matchPath({ path: `/${locale}/preferences/trips/hotels` }, pathname);

  const isFlightsBookingHistory =
    matchPath({ path: `/preferences/trips/flights` }, pathname) ||
    matchPath({ path: `/${locale}/preferences/trips/flights` }, pathname);

  const isPreferences =
    matchPath({ path: `/preferences/*` }, pathname) ||
    matchPath({ path: `/${locale}/preferences/*` }, pathname);

  const isCarRental = [`/${CAR_RENTAL_PATH_NAME}`, `/${locale}/${CAR_RENTAL_PATH_NAME}`].some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname),
  );

  const isRootHome =
    matchPath({ path: '/', end: true }, pathname) ||
    matchPath({ path: `/${locale}`, end: true }, pathname);

  if (isFlightConfirmation) return 'Flights Confirmation';
  if (isFlightPaymentVerification) return 'Flights Payment Verification';
  if (isFlightBookingHistoryDetails) return 'Flights Booking History Details';
  if (isFlightBooking) return 'Flights Booking';
  if (isFlightFareComparison) return 'Flights Fare Comparison';
  if (isFlightTripDetailUser) return 'Flights Trip Detail (Real User)';
  if (isFlightTripDetail) return 'Flights Trip Detail (Internal User)';
  if (isFlightResults) return 'Flights Results';

  if (isHotelConfirm) return 'Hotel Confirmation';
  if (isHotelCheckout) return 'Hotel Checkout';
  if (isHotelReview) return 'Hotel Booking Review';
  if (isHotelDetail) return 'Hotel Detail';
  if (isHotelResults) return 'Hotels Results';

  if (isProfilePaymentMethods) return 'Profile Payment Methods';
  if (isProfilePersonalInfo) return 'Profile Personal Info';
  if (isProfileTravellers) return 'Profile Travellers';
  if (isHotelsBookingHistory) return 'Hotels Booking History';
  if (isFlightsBookingHistory) return 'Flights Booking History';
  if (isPreferences) return 'Preferences';
  if (isCarRental) return 'Car Rentals';

  if (isFlightsHome) return 'Flights Home';
  if (isHotelsHome) return 'Hotels Home';
  if (isRootHome) return 'Home';

  return 'Unknown View';
}
